"use client";

import {
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  useTransition,
} from "react";
import { useLocale } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { type Locale, locales, isPublicLocale } from "@/i18n/routing";
import { LANGUAGES } from "@/i18n/languages";
import {
  readPreferredLocale,
  savePreferredLocale,
} from "@/i18n/preferred-locale";
import { detectBrowserLocale } from "@/i18n/detect-browser-locale";
import { updateUserProfile } from "@/app/actions/auth";
import { useAuth } from "@/contexts/AuthContext";
import { X } from "lucide-react";
import "flag-icons/css/flag-icons.min.css";

/**
 * 하드코딩 예외: 배너 문구는 "현재 locale" 이 아니라 "감지된 브라우저 언어" 로 보여줘야
 * 의미가 있는데(일본어 사용자에게 한국어로 "일본어로 보시겠어요?" 라고 물으면 못 읽는다),
 * next-intl 은 현재 locale 번들만 로드하므로 목표 언어 문구를 가져올 수 없다.
 * 따라서 이 5줄만 예외적으로 컴포넌트에 직접 둔다.
 */
const BANNER_COPY: Record<Locale, { title: string; cta: string; dismiss: string }> = {
  ko: { title: "한국어로 보시겠어요?", cta: "변경", dismiss: "닫기" },
  en: { title: "View in English?", cta: "Switch", dismiss: "Dismiss" },
  ja: { title: "日本語で表示しますか？", cta: "切替", dismiss: "閉じる" },
  "zh-CN": { title: "以简体中文浏览？", cta: "切换", dismiss: "关闭" },
  "zh-TW": { title: "以繁體中文瀏覽？", cta: "切換", dismiss: "關閉" },
};

// sessionStorage 기준 — 닫기는 이 세션(탭) 동안만 기억하고 다음 세션엔 다시 제안한다
// (AppInstallBanner 와 동일 패턴).
const DISMISS_KEY = "locarora-lang-suggest-dismissed";

// 배너를 띄우는 경로. 홈·상품 상세·검색결과만 — 결제/예약/프로필처럼 진행 중인 플로우에서
// locale 이 바뀌면 사용자가 맥락을 잃는다.
function isSuggestablePath(pathname: string): boolean {
  return (
    pathname === "/" ||
    pathname.startsWith("/products/") ||
    pathname.startsWith("/s/")
  );
}

// top 오프셋은 rem 하드코딩이 아니라 DOM 실측으로 구한다. 헤더 위쪽에 flow 로 끼어드는
// 요소(AppInstallBanner)가 있으면 헤더·검색바가 통째로 밀려서 고정값이 검색바를 덮었다.
// sticky nav 의 rect.bottom 은 safe-area·설치 배너·스크롤 상태를 이미 전부 반영하므로
// 그 값(그리고 홈에서는 검색바 블록 하단)만 보면 데스크탑/모바일 분기도 필요 없다.
const BANNER_GAP_PX = 8;

/** 헤더(+홈 검색바) 하단 y좌표 + 여백. 앵커를 못 찾으면 헤더 높이 대략치로 폴백. */
function measureTop(): number {
  const nav = document.querySelector("[data-main-nav]");
  const search = document.querySelector("[data-home-search-block]");
  const navBottom = nav?.getBoundingClientRect().bottom ?? 60;
  // 홈 검색바는 비-sticky라 스크롤하면 navBottom 아래로 내려가고, 그때부터 nav 기준이 된다.
  const searchBottom = search
    ? search.getBoundingClientRect().bottom
    : -Infinity;
  return Math.max(navBottom, searchBottom) + BANNER_GAP_PX;
}

// 노출 판정은 localStorage/sessionStorage/navigator 에만 의존하므로 SSR 에서는 계산할 수
// 없다. useEffect + setState 로 채우면 react-hooks/set-state-in-effect 에 걸리고 cascading
// render 도 생기므로, "하이드레이션 완료" 신호만 useSyncExternalStore 로 받아 렌더 중에
// 동기 계산한다(서버 스냅샷 false → 첫 렌더는 서버와 동일하게 미노출).
const subscribeHydrated = () => () => {};
const getHydratedSnapshot = () => true;
const getHydratedServerSnapshot = () => false;

/**
 * 제안할 언어를 고르는 순수 판정. 제안하지 않아야 하는 모든 경우는 null.
 *
 * 우선순위 = 사용자가 가장 분명하게 밝힌 선택 순:
 *  1. 이 기기에서 고른 언어(상단 언어 메뉴·프로필 언어 설정 모두 저장)
 *  2. 프로필 선호 언어(로그인 사용자) — 단, 기기 언어와 같을 때만. 가입 때 언어를
 *     못 정하면 "en" 이 기본으로 들어가서, 프로필 값이 본인 선택이 아닐 수 있다.
 *  3. 브라우저 언어(아무 선택도 없을 때의 추정)
 *
 * 1·2 는 예전엔 "있기만 하면" 배너를 껐다. 그런데 선택한 언어와 지금 보는 페이지
 * 언어가 다를 수 있다 — 다른 언어로 된 링크를 타고 들어온 경우. 실제로 프로필이
 * ja 인 사용자가 /ko/ 페이지를 기기 번역기로 보다가 번역기가 무한 재귀로 죽었다
 * (LOCARORA-WEB-6S). 그래서 이제는 "선택한 언어 == 현재 언어"일 때만 끄고,
 * 다르면 그 선택한 언어로 보기를 제안한다. 메뉴로 일부러 고른 언어 위에서는
 * 1 이 현재 언어와 같으므로 여전히 뜨지 않는다.
 */
export function pickLanguageSuggestion(input: {
  locale: string;
  pathname: string;
  storedPreference: Locale | null;
  profilePreference: string | null;
  dismissedThisSession: boolean;
  browserLocale: Locale | null;
}): Locale | null {
  const { locale, pathname, storedPreference, profilePreference } = input;

  const profile = (locales as readonly string[]).includes(profilePreference ?? "")
    ? (profilePreference as Locale)
    : null;

  let target: Locale | null;
  if (storedPreference) {
    // An explicit pick on this device is the strongest signal.
    target = storedPreference;
  } else if (profile) {
    // The profile value is not always a choice: signup falls back to "en"
    // when it cannot tell the language. Act on it only when the device agrees,
    // otherwise stay silent as before (no browser-locale guess either).
    target = profile === input.browserLocale ? profile : null;
  } else {
    target = input.browserLocale;
  }

  // 숨김 출시 중인 언어로는 이동을 제안하지 않는다
  if (!target || target === locale || !isPublicLocale(target)) return null;
  if (input.dismissedThisSession) return null;
  // 배너 허용 경로만 — 진행 중인 플로우에서 언어를 바꾸면 맥락을 잃는다.
  if (!isSuggestablePath(pathname)) return null;
  return target;
}

function resolveSuggestion(
  locale: string,
  pathname: string,
  userPreferredLanguage: string | null,
): Locale | null {
  let dismissedThisSession = false;
  try {
    dismissedThisSession = Boolean(sessionStorage.getItem(DISMISS_KEY));
  } catch {
    // 스토리지 차단(시크릿 모드 등) — 닫은 적 없는 것으로 취급하고 계속 진행
  }
  return pickLanguageSuggestion({
    locale,
    pathname,
    storedPreference: readPreferredLocale(),
    profilePreference: userPreferredLanguage,
    dismissedThisSession,
    browserLocale: detectBrowserLocale() ?? null,
  });
}

/**
 * 브라우저 언어가 현재 보고 있는 locale 과 다를 때 전환을 제안하는 상단 플로팅 배너.
 *
 * 순수 클라이언트 컴포넌트로만 동작한다 — 쿠키/headers()/getLocale() 이나 신규 서버 페치를
 * 쓰면 이 컴포넌트를 마운트한 layout 전체가 동적 렌더로 떨어져 ISR 이 깨진다.
 */
export function LanguageSuggestBanner() {
  const locale = useLocale();
  // 로그인 사용자가 프로필에 저장해 둔 언어(AuthProvider). 지금 페이지 언어와 같으면
  // 미노출, 다르면 그 언어를 제안한다(pickLanguageSuggestion). 로그인 확인 전(loading)
  // 에는 판정하지 않는다 — 로그인 사용자에게 배너가 잠깐 떴다 사라지는 걸 막는다.
  const { status, user } = useAuth();
  const authResolved = status !== "loading";
  const userPreferredLanguage = user?.preferred_language ?? null;
  // next-intl 의 usePathname 은 locale prefix 가 제거된 경로("/products/123")를 준다.
  const pathname = usePathname();
  const router = useRouter();
  const [, startTransition] = useTransition();

  const hydrated = useSyncExternalStore(
    subscribeHydrated,
    getHydratedSnapshot,
    getHydratedServerSnapshot,
  );
  // 수락/닫기 후 즉시 감추기 위한 플래그. 두 핸들러 모두 localStorage/sessionStorage 에
  // 흔적을 남기므로 이후 재계산에서도 계속 null 이 된다(리셋 불필요).
  const [closed, setClosed] = useState(false);
  // 실측 전(null)에는 렌더하지 않는다 — 임시 좌표로 그렸다가 옮기면 점프가 보인다.
  const [topPx, setTopPx] = useState<number | null>(null);

  // 감지 결과(= 제안할 언어). null 이면 미노출.
  const target = useMemo(
    () =>
      hydrated && authResolved && !closed
        ? resolveSuggestion(locale, pathname, userPreferredLanguage)
        : null,
    [hydrated, authResolved, closed, locale, pathname, userPreferredLanguage],
  );

  // 스크롤(검색바가 화면 밖으로) · 리사이즈 · 레이아웃 변화에 따라 헤더 하단이 움직이므로
  // 계속 다시 잰다. AppInstallBanner 닫기처럼 스크롤/리사이즈 없이 헤더가 위로 당겨지는
  // 경우는 ResizeObserver(body 높이 변화)가 잡는다.
  // setState 는 rAF 콜백 안에서만 호출(set-state-in-effect 회피).
  useEffect(() => {
    if (!target) return;
    let raf = 0;
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        setTopPx(measureTop());
        raf = 0;
      });
    };
    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    const ro = new ResizeObserver(schedule);
    ro.observe(document.body);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [target]);

  if (!target || topPx === null) return null;

  const copy = BANNER_COPY[target];
  const flagCode = LANGUAGES.find((lang) => lang.code === target)?.flagCode;

  const handleAccept = () => {
    savePreferredLocale(target);
    // DB 에도 저장 (fire-and-forget). 비로그인은 LOGIN_REQUIRED 가 돌아오고 무시된다
    // — LanguageDrawer 와 동일 패턴.
    updateUserProfile({ preferred_language: target });

    // useSearchParams 훅 대신 window 에서 직접 — layout 마운트 컴포넌트가 훅을 쓰면
    // Suspense 경계 요구/CSR bailout 이슈가 생긴다.
    const query = Object.fromEntries(new URLSearchParams(window.location.search));

    startTransition(() => {
      const href =
        Object.keys(query).length > 0 ? { pathname, query } : pathname;
      router.replace(href, { locale: target });
    });
    setClosed(true);
  };

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, target);
    } catch {
      // 저장 실패 시엔 다음 렌더에 다시 뜰 수 있으나 기능상 문제 없음
    }
    setClosed(true);
  };

  return (
    <div
      lang={target}
      style={{ top: topPx }}
      // z-45: sticky nav(z-50) 아래 · 검색 필터바(z-40) 위
      className="fixed z-45 left-3 right-3 md:left-auto md:right-6 md:w-auto flex items-center gap-2 rounded-full border border-gray-200 bg-white py-1.5 pl-3.5 pr-1.5 shadow-lg animate-in slide-in-from-top-4 fade-in duration-300"
    >
      {flagCode && (
        <span
          className={`fi fi-${flagCode} w-5! h-auto shrink-0 bg-cover! border border-gray-300`}
        />
      )}
      <p className="flex-1 min-w-0 truncate text-[13px] font-semibold text-gray-900">
        {copy.title}
      </p>
      <button
        type="button"
        onClick={handleAccept}
        className="h-8 shrink-0 rounded-full bg-orange-500 px-3.5 text-xs font-bold text-white hover:bg-orange-600"
      >
        {copy.cta}
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={copy.dismiss}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
      >
        <X size={15} />
      </button>
    </div>
  );
}
